import {
	Anchor,
	Badge,
	Card,
	Divider,
	Group,
	Stack,
	Text,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { assistantLogDetailTexts } from "@/locales/assistant-log-detail";
import { i18nStore } from "@/store/i18n";
import type {
	AssistantLogConversationMetaDto,
	AssistantLogTranscriptDto,
} from "@/types/ai-assistant-log";
import {
	formatLogCost,
	formatLogCount,
	formatLogDate,
	formatLogTime,
	formatLogTokens,
	logDisplayName,
	logModalityLabel,
} from "./log-format.logic";
import { LogVendorBadge } from "./log-vendor-badge";

/** Panel kanan lapis 3: ringkasan percakapan, model, sumber data, dan catatan retensi. */
export function LogTranscriptInfo({
	meta,
	user,
	retentionDays,
}: {
	meta: AssistantLogConversationMetaDto;
	user: AssistantLogTranscriptDto["user"] | null;
	retentionDays: number;
}) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const td = assistantLogDetailTexts[lang];

	const rows = [
		{
			label: td.infoUser,
			value: user ? logDisplayName(user.name, user.email) : t.none,
		},
		{
			label: td.infoCreated,
			value: `${formatLogDate(meta.startedAt, lang)} · ${formatLogTime(meta.startedAt, lang)}`,
		},
		{
			label: td.infoLastMessage,
			value: `${formatLogDate(meta.lastMessageAt, lang)} · ${formatLogTime(meta.lastMessageAt, lang)}`,
		},
		{ label: td.infoMessages, value: formatLogCount(meta.messages, lang) },
		{ label: td.infoTurns, value: formatLogCount(meta.turns, lang) },
		{
			label: td.infoModalities,
			value:
				meta.modalities.map((m) => logModalityLabel(m, t)).join(", ") || t.none,
		},
		{
			label: td.infoPageRoutes,
			value: meta.pageRoutes.join(", ") || t.none,
		},
		{
			label: td.infoTokens,
			value: formatLogTokens(meta.inputTokens, meta.outputTokens, lang),
		},
		{ label: td.infoCost, value: formatLogCost(meta.cost, t, lang) },
	];

	return (
		<Card withBorder radius="md" p="lg" data-log-transcript-info>
			<Stack gap="sm">
				<Text fw={700}>{td.infoTitle}</Text>

				{rows.map((row) => (
					<Group key={row.label} justify="space-between" gap="xs" wrap="nowrap">
						<Text size="xs" c="dimmed">
							{row.label}
						</Text>
						<Text size="xs" fw={600} ta="right" lineClamp={2}>
							{row.value}
						</Text>
					</Group>
				))}

				<Divider />
				<Text size="xs" c="dimmed" fw={600} tt="uppercase">
					{td.infoModels}
				</Text>
				{meta.models.length === 0 ? (
					<Text size="xs" c="dimmed">
						{t.none}
					</Text>
				) : (
					meta.models.map((use) => (
						<LogVendorBadge key={use.model ?? "unknown"} use={use} showTurns />
					))
				)}

				<Divider />
				<Text size="xs" c="dimmed" fw={600} tt="uppercase">
					{td.infoTools}
				</Text>
				{meta.tools.length === 0 ? (
					<Text size="xs" c="dimmed">
						{t.none}
					</Text>
				) : (
					<Group gap={4} wrap="wrap">
						{meta.tools.map((tool) => (
							<Badge key={tool.name} variant="outline" size="sm" ff="monospace">
								{tool.name} {td.uses(String(tool.uses))}
							</Badge>
						))}
					</Group>
				)}

				<Divider />
				<Anchor component={Link} to="/admin/audit-log" size="xs">
					{td.openAuditLog}
				</Anchor>
				<Text size="xs" c="dimmed">
					{td.retentionNote(retentionDays)}
				</Text>
			</Stack>
		</Card>
	);
}
